"use client";

import { AuthGuard } from "./AuthGuard";
import { LayoutFooter, LayoutHeader, ImpersonateBanner, MobileBottomNav, LayoutTopHeader } from "@repo/ui";
import { useAuth } from "../hooks/useAuth";
import { useImpersonateState, useStopImpersonation } from "../hooks/useImpersonate";
import { useFooterLinks } from "../hooks/useFooterLinks";
import { useTournaments, useTournament } from "../hooks/useTournaments";
import { useClubContact, useTournamentFinance, useTournamentSalesRepresentative } from "../hooks/useTournamentDetails";
import { BotpressChat } from "./BotpressChat";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const PUBLIC_PATHS = [
  "/login",
  "/login/forgot-password",
  "/login/create-password",
  "/add-guest",
];

export function LayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  
  const isPublicPath = pathname && PUBLIC_PATHS.includes(pathname);

  // For public paths, render without authentication requirements
  if (isPublicPath) {
    return (
      <>
      <LayoutTopHeader />
      <main className="container py-8">
        {children}
      </main>
      </> 
    );
  }

  // For private paths, use the PrivateLayoutContent component
  return <PrivateLayoutContent>{children}</PrivateLayoutContent>;
}

function PrivateLayoutContent({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const { isAuthenticated, logout, user } = useAuth();
  const { footerData, loading, error } = useFooterLinks({ lang: i18n.language });
  const { isImpersonating, impersonatedEmail } = useImpersonateState();
  const { mutate: stopImpersonation } = useStopImpersonation();
  const pathname = usePathname();
  
  const isPublicPath = pathname && PUBLIC_PATHS.includes(pathname);

  // Always call hooks - extract tournament ID
  const currentTournamentId = useMemo(() => {
    if (!pathname || isPublicPath) return null;
    
    const pathSegments = pathname.split('/');
    // Check for patterns like /booking/[id] or /tournament/[id]
    if (pathSegments[1] === 'booking' && pathSegments[2]) {
      return pathSegments[2];
    }
    if (pathSegments[1] === 'tournament' && pathSegments[2]) {
      return pathSegments[2];
    }
    return null;
  }, [pathname, isPublicPath]);

  // Always call all hooks
  const tournamentsResult = useTournaments();
  const currentTournamentResult = useTournament(currentTournamentId || '');
  const clubContactResult = useClubContact();
  const salesRepResult = useTournamentSalesRepresentative(currentTournamentId || '');
  const { data: financeData } = useTournamentFinance(currentTournamentId || '');

  // Use default values for public paths
  const finalTournamentsData = isPublicPath
    ? { tournaments: [], isLoading: false }
    : tournamentsResult;
  const finalCurrentTournamentData = isPublicPath
    ? { tournament: null, isLoading: false }
    : currentTournamentResult;
  const finalClubContactData = isPublicPath
    ? { data: null, isLoading: false }
    : clubContactResult;
  const finalSalesRepData = isPublicPath
    ? { data: null, isLoading: false }
    : salesRepResult;

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => {
        // Redirect to login with logout success parameter
        window.location.href = '/login?logout=success';
      }
    });
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
  };

  return (
    <>
      <LayoutHeader
        tournaments={finalTournamentsData.tournaments.tournaments}
        currentTournament={finalCurrentTournamentData.tournament}
        tournamentsLoading={finalTournamentsData.isLoading}
        currentTournamentLoading={finalCurrentTournamentData.isLoading}
        clubContact={finalClubContactData.data || undefined}
        contactLoading={finalClubContactData.isLoading}
        salesRepresentative={finalSalesRepData.data || undefined}
        salesRepresentativeLoading={finalSalesRepData.isLoading}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        userRoles={user?.impersonatedRoles || user?.roles || []}
        userName={user?.email as string | undefined}
      />
      {isImpersonating && impersonatedEmail && (
        <ImpersonateBanner
          impersonatedEmail={impersonatedEmail}
          onStopImpersonation={handleStopImpersonation}
        />
      )}
      <main className="container pb-20 md:pb-0">
        <AuthGuard>{children}</AuthGuard>
      </main>
      <LayoutFooter
          footerData={footerData}
          loading={loading}
          error={error}
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
        />
      <MobileBottomNav />
      <BotpressChat
        user={user}
        crsReferenceNumber={financeData?.clubTournament.crsReferenceNumber}
      />
    </>
  );
}